// app/frontend/pages/Register.tsx
import { Head, usePage } from '@inertiajs/react';
import { useForm } from '@mantine/form';
import { useState } from 'react';
import {
  Container,
  Title,
  TextInput,
  PasswordInput,
  Paper,
  Button,
  Stack,
  Anchor,
  Box,
  MultiSelect,
  MantineProvider,
  createTheme,
  MantineTheme,
} from '@mantine/core';
import { Notifications, showNotification } from '@mantine/notifications';
import { IconAlertCircle, IconCheck } from '@tabler/icons-react';

import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';
import '../styles/Login-form.css';

// Custom Mantine theme
const theme = createTheme({
  primaryColor: 'orange',
  colors: {
    orange: [
      '#FEF5ED',
      '#FDE3D1',
      '#FCB693',
      '#FA9D73',
      '#F87F4D',
      '#F66A33',
      '#EA580C',
      '#C44B0A',
      '#9E3D08',
      '#783006',
    ],
  },
  fontFamily: 'Poppins, sans-serif',
  headings: {
    fontFamily: 'Poppins, sans-serif',
  },
});

type RegisterPageProps = {
  roleOptions: { label: string; value: string }[];
};

export default function Register() {
  const [isLoading, setIsLoading] = useState(false);
  const { props } = usePage<RegisterPageProps>();
  const { roleOptions } = props;

  // Use Mantine's form hook with real-time validation
  const form = useForm({
    initialValues: {
      username: '',
      email: '',
      role_ids: [] as string[],
      password: '',
      password_confirmation: '',
    },
    validate: {
      username: (value) => {
        const usernameRegex = /^[a-z0-9_-]+$/;
        const reservedUsernames = ['admin', 'root', 'moderator', 'support', 'system'];
        const bannedWords = ['offensive1', 'offensive2', 'swearword']; // Expand list

        if (value.length < 6) return 'Username must be at least 6 characters';
        if (value.length > 30) return 'Username must be at most 30 characters';
        if (!usernameRegex.test(value)) return 'Username can only contain lowercase letters, numbers, -, and _';
        if (/^\d+$/.test(value)) return 'Username cannot be only numbers';
        if (value.includes('--') || value.includes('__') || value.includes('-_') || value.includes('_-')) {
          return 'Username cannot have consecutive special characters like -- or __';
        }
        if (/^[-_]/.test(value) || /[-_]$/.test(value)) return 'Username cannot start or end with - or _';
        if (reservedUsernames.includes(value)) return 'This username is not allowed';
        if (bannedWords.some((word) => value.includes(word))) return 'This username is not allowed';
        if (/<|>|&|;|'|"/.test(value)) return 'Invalid characters in username';

        return null;
      },
      email: (value) => (/^\S+@\S+$/.test(value) ? null : 'Invalid email'),
      password: (value) => (value.length >= 6 ? null : 'Password must be at least 6 characters'),
      password_confirmation: (value, values) => (value === values.password ? null : 'Passwords do not match'),
    },
    validateInputOnChange: true,
    validateInputOnBlur: true,
  });

  const handleSubmit = async (values: typeof form.values) => {
    setIsLoading(true);
    const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content;

    try {
      const formData = new FormData();
      formData.append('user[username]', values.username);
      formData.append('user[email]', values.email);
      values.role_ids.forEach((roleId) => formData.append('user[role_ids][]', roleId));
      formData.append('user[password]', values.password);
      formData.append('user[password_confirmation]', values.password_confirmation);

      const response = await fetch('/register', {
        method: 'POST',
        headers: {
          'X-CSRF-Token': csrfToken || '',
        },
        credentials: 'include',
        body: formData,
      });

      if (response.ok) {
        showNotification({
          color: 'green',
          title: 'Registration Successful',
          message: 'Check your email for confirmation instructions.',
          icon: <IconCheck />,
          autoClose: 3000,
        });
        // Redirect to login after a brief delay
        setTimeout(() => {
          window.location.href = '/login';
        }, 1500);
      } else {
        const data = await response.json().catch(() => ({}));
        showNotification({
          color: 'red',
          title: 'Registration Failed',
          message: data.error || 'There was a problem with registration',
          icon: <IconAlertCircle />,
          autoClose: 5000,
        });
      }
    } catch (error) {
      showNotification({
        color: 'red',
        title: 'Error',
        message: 'An error occurred during registration',
        icon: <IconAlertCircle />,
        autoClose: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <MantineProvider theme={theme}>
      <Notifications position="top-right" />
      <Head title="Sign Up" />
      <Container size="xs" my="xl" style={{ minHeight: '75vh' }}>
        <Box>
          <Paper
            shadow="md"
            p={30}
            radius="md"
            className="mb-4"
            style={{
              backgroundColor: '#75757530',
              marginBottom: '20px',
            }}
            sx={(theme) => ({
              width: '100%',
              maxWidth: '400px',
              margin: '0 auto',
              marginTop: '40px',
              [theme.fn.smallerThan('sm')]: {
                padding: '20px',
                marginTop: '20px',
              },
            })}
          >
            <Title order={1} ta="center" mb="md" c="white">
              Sign Up
            </Title>
            <form onSubmit={form.onSubmit(handleSubmit)}>
              <Stack>
                <TextInput
                  label="Username"
                  placeholder="Your username"
                  required
                  styles={{
                    label: {
                      fontSize: 16,
                      fontWeight: 500,
                      color: '#fff',
                    },
                  }}
                  disabled={isLoading}
                  {...form.getInputProps('username')}
                />

                <TextInput
                  label="Email"
                  placeholder="your@email.com"
                  required
                  styles={{
                    label: {
                      fontSize: 16,
                      fontWeight: 500,
                      color: '#fff',
                    },
                  }}
                  disabled={isLoading}
                  {...form.getInputProps('email')}
                />

                <TextInput
                  label="Confirm Your Email"
                  placeholder="your@email.com"
                  required
                  styles={{
                    label: {
                      fontSize: 16,
                      fontWeight: 500,
                      color: '#fff',
                    },
                  }}
                  disabled={isLoading}
                  {...form.getInputProps('email')}
                />

                <MultiSelect
                  label="How do you identify in the dance scene?"
                  placeholder={
                    form.values.role_ids && form.values.role_ids.length > 0 ? '' : 'Select one or more roles'
                  }
                  data={roleOptions}
                  hidePickedOptions
                  styles={{
                    label: {
                      fontSize: 16,
                      fontWeight: 500,
                      color: '#fff',
                    },
                  }}
                  disabled={isLoading}
                  {...form.getInputProps('role_ids')}
                />

                <PasswordInput
                  label="Password"
                  placeholder="Your password"
                  required
                  disabled={isLoading}
                  styles={{
                    label: {
                      fontSize: 16,
                      fontWeight: 500,
                      color: '#fff',
                    },
                    input: {
                      fontSize: 16,
                    },
                  }}
                  {...form.getInputProps('password')}
                />

                <PasswordInput
                  label="Confirm Password"
                  placeholder="Confirm your password"
                  required
                  styles={{
                    label: {
                      fontSize: 16,
                      fontWeight: 500,
                      color: '#fff',
                    },
                    input: {
                      fontSize: 16,
                    },
                  }}
                  disabled={isLoading}
                  {...form.getInputProps('password_confirmation')}
                />

                <Button type="submit" size="lg" fullWidth loading={isLoading} className="mt-4">
                  Sign up
                </Button>
              </Stack>
            </form>
            <Stack mt="md" gap="xs" className="login-links mt-4">
              <Anchor href="/login">Have an account already? Login here!</Anchor>
              <Anchor href="/confirmation/new">Didn't receive confirmation instructions?</Anchor>
            </Stack>
          </Paper>
        </Box>
      </Container>
    </MantineProvider>
  );
}
